<template>
  <div class="composite-reporting">
    <LayoutContainer>
      <div class="composite-reporting-container">
        <div class="composite-reporting-form">
          <div class="composite-reporting-title">
            <h3>Ajouter un reporting</h3>
          </div>
          <div class="composite-reporting-content">
            <div class="inputs">
              <InputSelect
                id="test"
                class="recurrence-select"
                novalue="Récurrence"
                label=""
                smallDropdown
                :options="[
                  { label: 'Semaine', value: '1' },
                  { label: 'Mois', value: '2' },
                  { label: 'Trimestre', value: '3' },
                  { label: 'Semestre', value: '4' },
                  { label: 'Année', value: '5' }
                ]"
                v-model="reccurence"
                uptitle
              />
              <InputCommon
                v-model="recipients"
                label=""
                placeholder="MAILS DESTINATAIRES*"
                uptitle
                hint="*Séparés par une virgule"
              />
            </div>
          </div>
          <CommonButton class="btn btn-primary" @click="submit()">
            Ajouter <ArrowRight />
          </CommonButton>
        </div>

        <div class="composite-reporting-list">
          <div class="composite-reporting-title">
            <h3>Vos reportings</h3>
          </div>
          <div
            v-if="store.getTenantReporting.length > 0"
            :class="{ overflow: store.getTenantReporting.length > 3 }"
          >
            <div v-for="reporting in store.getTenantReporting">
              <div class="reporting-header">
                <CommonTag classes="blue"> {{ getReccurence(parseInt(reporting.type)) }}</CommonTag>
                <CommonTag classes="gray-4">
                  Prochain envoi : {{ formatDate(reporting.nextSend!) }}
                </CommonTag>
                <Trash class="icon-trash" @click="deleteReporting(reporting.id!)" />
              </div>
              <div>
                <span class="gray-2" v-for="(email, index) in reporting.emails" :key="index">
                  {{ email }}<span v-if="index !== reporting.emails.length - 1">, </span>
                </span>
              </div>
            </div>
          </div>
          <CommonTag v-else classes="black"> Aucun reporting</CommonTag>
        </div>
      </div>
    </LayoutContainer>
  </div>
</template>

<script setup lang="ts">
import { ArrowRight, Trash } from 'lucide-vue-next'
import InputSelect from '@/components/common/input/InputSelect.vue'
import CommonButton from '@/components/common/CommonButton.vue'
import CommonTag from '../common/CommonTag.vue'
import InputCommon from '@/components/common/input/InputCommon.vue'
import { onMounted, ref } from 'vue'
import LayoutContainer from '../layout/LayoutContainer.vue'
import { useReportingStore } from '@/stores/store_reporting'

const reccurence = ref<string>()
const recipients = ref<string>('')

const store = useReportingStore()
const types: Record<number, string> = {
  1: 'Hebdomadaire',
  2: 'Mensuel',
  3: 'Trimestriel',
  4: 'Semestriel',
  5: 'Annuel'
}
function submit() {
  const valid = checkEmails()
  if (!valid) {
    alert('Les emails ne sont pas correctement formatés.')
    return
  }
  if (!reccurence.value) {
    alert('Veuillez choisir une réccurence.')
    return
  }
  const report = {
    type: reccurence.value!,
    emails: recipients.value.split(',')
  }
  store.postReporting(report)

  reccurence.value = ''
  recipients.value = ''
}

function checkEmails() {
  if (!recipients.value) return false
  const cleaned = recipients.value.trim().replace(/\s+/g, '')
  if (cleaned.startsWith(',') || cleaned.endsWith(',') || cleaned.includes(',,')) return false
  const emails = cleaned.split(',')
  const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/
  return emails.every((email) => emailRegex.test(email))
}

function getReccurence(type: number) {
  return types[type] ?? ''
}

function deleteReporting(id: number) {
  if (confirm('Confirmer la suppresion du rapport ?')) {
    store.deleteReporting(id)
  }
}

function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('fr-FR')
}

onMounted(() => {
  store.fetchTenantReportings()
})
</script>

<style scoped lang="scss">
@import '@/assets/mixins.scss';
.composite-reporting {
  background-color: var(--vc-gray1);
  padding-top: 48px;
  padding-bottom: 48px;

  &-container {
    display: flex;
  }

  &-form {
    padding-right: var(--spacer-3);
    border-right: 1px solid var(--vc-secondary-0_2);
  }

  &-list {
    padding-left: var(--spacer-3);

    ul {
      list-style: none;
      padding-inline-start: 0;

      li {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacer-0_5) var(--spacer);
        align-items: center;
        margin-bottom: var(--spacer);
      }
    }
  }

  &-form,
  &-list {
    width: 50%;
  }

  &-title {
    margin-bottom: var(--spacer-1_5);
  }

  &-content {
    display: flex;
    align-items: center;
    gap: var(--spacer-0_5);
    margin-bottom: var(--spacer-1_5);

    .inputs {
      @include hlayout(10px);
      width: 100%;
      margin-right: var(--spacer);
      align-items: flex-start;

      .input-select,
      .input-common {
        width: 100%;
      }
    }
  }

  .input-select {
    background-color: var(--vc-white);
  }
}

.mobile {
  .composite-reporting-content .inputs {
    @include vlayout(10px);
  }

  .composite-reporting {
    &-container {
      flex-direction: column;
      gap: var(--spacer-2);
    }

    &-form,
    &-list {
      min-width: 100%;
    }

    &-form {
      border: none;
      padding-right: 0;
    }

    &-list {
      padding-left: 0;
    }
  }

  .inputs {
    margin-right: 0;
  }
}

.icon-trash {
  width: 17px;
  height: 17px;
  color: red;
  border-radius: 5px;
  cursor: pointer;
}

.overflow {
  overflow-y: auto;
  height: 150px;
}

.reporting-list {
  display: flex;
  flex-direction: column;
}

.reporting-header {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.reporting-header + div {
  margin-bottom: 15px;
}
</style>
